import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Baby, Users, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classData: any;
}

export default function ClassDetailDialog({ open, onOpenChange, classData }: Props) {
  const { data: students, isLoading } = useQuery({
    queryKey: ["class-students", classData?.id],
    queryFn: async () => {
      if (!classData?.id) return [];
      const { data } = await supabase.from("children").select("id, full_name, date_of_birth, photo_url, parent_name").eq("class_id", classData.id).order("full_name");
      return data || [];
    },
    enabled: !!classData?.id && open,
  });

  if (!classData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookIcon className="w-5 h-5 text-primary" />
            {classData.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            {classData.age_group && <Badge variant="secondary">{classData.age_group}</Badge>}
            <Badge variant="outline" className="gap-1"><Users className="w-3 h-3" /> السعة: {classData.capacity || 25}</Badge>
            <Badge variant="outline" className="gap-1"><Baby className="w-3 h-3" /> الطلاب: {students?.length || 0}</Badge>
          </div>

          {classData.staff?.full_name && (
            <p className="text-sm text-muted-foreground">المعلمة: <span className="font-medium text-foreground">{classData.staff.full_name}</span></p>
          )}

          <div>
            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Baby className="w-4 h-4" /> قائمة الطلاب
            </h4>
            {isLoading ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : !students?.length ? (
              <p className="text-sm text-muted-foreground text-center py-4">لا يوجد طلاب في هذا الفصل</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {students.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">{s.full_name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{s.full_name}</p>
                      {s.parent_name && <p className="text-xs text-muted-foreground">ولي الأمر: {s.parent_name}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    </svg>
  );
}
